import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Tooltip
} from '@mui/material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart
} from 'recharts';
import { 
  TrendingUp, 
  People, 
  Warning, 
  CalendarMonth,
  Refresh,
  Assessment,
  CheckCircle,
  Error,
  Info,
  EventAvailable
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const PredictionDashboard = () => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [yearPredictions, setYearPredictions] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [holidayData, setHolidayData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [predicting, setPredicting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [tabValue, setTabValue] = useState(0);

  const COLORS = {
    High: '#ff4444',
    Medium: '#ffaa00',
    Low: '#00c853',
    predicted: '#ff4d4f',
    actual: '#007bff',
    holidays: '#9c27b0'
  };

  const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  useEffect(() => {
    fetchEmployees();
    fetchYearPredictions();
    fetchHolidayData();
  }, [selectedYear]);

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      const employee_id = payload.employee_id;
      const response = await fetch(`${API_URL}/api/predictions/active?employee_id=${employee_id || ''}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
           
          }
        }
      );
      const data = await response.json();
      setEmployees(data.data || []);
    } catch (error) {
      //console.error('Error fetching employees:', error);
    }
  };

  const fetchHolidayData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      const response = await fetch(`${API_URL}/api/holidays`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
      
          }
        }
      );
      const data = await response.json();
      
      if (data.holidayList && data.holidayList.holidays) {
        const holidays = JSON.parse(data.holidayList.holidays);
        
        // Count holidays per month
        const monthlyHolidayCounts = Array(12).fill(0);
        holidays.forEach(holiday => {
          const date = new Date(holiday.date + 'T00:00:00');
          if (date.getFullYear() === selectedYear) {
            const month = date.getMonth();
            monthlyHolidayCounts[month]++;
          }
        });
        
        setHolidayData({
          holidays: holidays,
          monthlyCounts: monthlyHolidayCounts,
          year: data.holidayList.year
        });
      } else {
        setHolidayData(null);
      }
    } catch (error) {
      //console.error('Error fetching holiday data:', error);
      setHolidayData(null);
    }
  };

  const fetchYearPredictions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      const employee_id = payload.employee_id;
      
      const response = await fetch(`${API_URL}/api/predictions/year/${selectedYear}?employee_id=${employee_id || ''}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
           
          }
        }
      );
      const data = await response.json();
      
      if (data.status === 'success') {
        setYearPredictions(data.data || []);
        generateAnalytics(data.data || []);
      }
    } catch (error) {
      //console.error('Error fetching year predictions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePredictYear = async () => {
    try {
      setPredicting(true);
      setError('');
      setSuccess('');
      
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      const employee_id = payload.employee_id;
      
      const response = await fetch(`${API_URL}/api/predictions/predict/all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
         },
        body: JSON.stringify({ year: selectedYear, employee_id })
      });

      const data = await response.json();
      
      if (response.ok && data.status === 'success') {
        setSuccess(`Successfully generated predictions for ${data.data?.summary?.successful_predictions || 0} employee-months`);
        fetchYearPredictions();
      } else {
        if (data.code === 'AI_PLAN_UPGRADE_REQUIRED') {
          setError({
            message: data.message || 'AI features are not included in your current plan. Please upgrade to unlock AI.',
            severity: 'warning',
            upgrade_required: true
          });
          window.dispatchEvent(new CustomEvent("openUpgradeModal", { detail: { feature: "AI Leave Prediction" } }));
        } else if (data.code === 'AI_TOKENS_EXHAUSTED') {
          setError({
            message: `⚠️ ${data.message || 'You have used all your AI tokens for this billing cycle.'}`,
            severity: 'warning'
          });
        } else {
          setError({
            message: data.message || data.error || 'Failed to generate predictions',
            severity: 'error'
          });
        }
      }
    } catch (error) {
      //console.error('Error generating predictions:', error);
      setError('Failed to generate predictions');
    } finally {
      setPredicting(false);
    }
  };

  const generateAnalytics = (predictions) => {
    if (!predictions || predictions.length === 0) return;

    const riskCount = { High: 0, Medium: 0, Low: 0 };
    const leaveTypes = {};
    const monthlyData = {};
    
    predictions.forEach(pred => {
      riskCount[pred.risk_level] = (riskCount[pred.risk_level] || 0) + 1;
      
      const type = pred.likely_leave_type || 'Unknown';
      leaveTypes[type] = (leaveTypes[type] || 0) + 1;
      
      const month = pred.prediction_month;
      if (!monthlyData[month]) {
        monthlyData[month] = { predicted: 0, actual: 0 };
      }
      monthlyData[month].predicted += pred.predicted_leaves || 0;
      monthlyData[month].actual += pred.actual_leaves_taken || 0;
    });

    const totalEmployees = new Set(predictions.map(p => p.employee_id)).size;

    setAnalytics({
      summary: {
        totalEmployees,
        totalPredictions: predictions.length,
        highRisk: riskCount.High || 0,
        mediumRisk: riskCount.Medium || 0,
        lowRisk: riskCount.Low || 0
      },
      riskDistribution: Object.entries(riskCount).map(([name, value]) => ({
        name,
        value,
        percentage: ((value / predictions.length) * 100).toFixed(1)
      })),
      leaveTypes: Object.entries(leaveTypes).map(([type, count]) => ({
        type,
        count,
        percentage: ((count / predictions.length) * 100).toFixed(1)
      })),
      monthlyData: Object.entries(monthlyData).map(([month, data]) => ({
        month: MONTH_NAMES[parseInt(month) - 1],
        monthNum: parseInt(month),
        ...data,
        accuracy: data.actual > 0 ? (100 - Math.abs(data.predicted - data.actual) / data.actual * 100).toFixed(1) : 0
      })).sort((a, b) => a.monthNum - b.monthNum)
    });
  };

  const getEmployeeMonthlyData = () => {
    const employeePredictions = selectedEmployee 
      ? yearPredictions.filter(p => p.employee_id === selectedEmployee)
      : yearPredictions;
    
    return MONTH_NAMES.map((month, idx) => {
      if (selectedEmployee) {
        // Single employee data
        const pred = employeePredictions.find(p => p.prediction_month === idx + 1);
        const holidayCount = holidayData ? holidayData.monthlyCounts[idx] : 0;
        const actualLeaves = pred?.actual_leaves_taken || 0;
        
        return {
          month,
          predicted: (pred?.predicted_leaves || 0) + holidayCount,
          actualLeaves: actualLeaves,
          actual: actualLeaves + holidayCount,
          holidays: holidayCount,
          probability: pred?.prediction_probability || '0%',
          riskLevel: pred?.risk_level || 'Low'
        };
      } else {
        // Aggregate data for all employees
        const monthPredictions = employeePredictions.filter(p => p.prediction_month === idx + 1);
        const holidayCount = holidayData ? holidayData.monthlyCounts[idx] : 0;
        const totalPredicted = monthPredictions.reduce((sum, p) => sum + (p.predicted_leaves || 0), 0);
        const totalActualLeaves = monthPredictions.reduce((sum, p) => sum + (p.actual_leaves_taken || 0), 0);
        const employeeCount = monthPredictions.length;
        
        // Calculate average risk level
        const riskCounts = { High: 0, Medium: 0, Low: 0 };
        monthPredictions.forEach(p => {
          riskCounts[p.risk_level] = (riskCounts[p.risk_level] || 0) + 1;
        });
        const dominantRisk = Object.entries(riskCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'Low';
        
        return {
          month,
          predicted: totalPredicted + holidayCount,
          actualLeaves: totalActualLeaves,
          actual: totalActualLeaves + holidayCount,
          holidays: holidayCount,
          probability: employeeCount > 0 ? `${employeeCount} emp` : '0',
          riskLevel: dominantRisk,
          employeeCount
        };
      }
    });
  };

  const getRiskLevelColor = (risk) => COLORS[risk] || '#757575';

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Paper sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.98)', boxShadow: 3 }}>
          <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
            {data.month}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box sx={{ 
              width: 12, 
              height: 12, 
              borderRadius: '50%', 
              bgcolor: COLORS.predicted 
            }} />
            <Typography variant="body2" sx={{ color: COLORS.predicted, fontWeight: 500 }}>
              Predicted (Leaves + Holidays): {data.predicted} days
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box sx={{ 
              width: 12, 
              height: 12, 
              borderRadius: '50%', 
              bgcolor: COLORS.actual 
            }} />
            <Typography variant="body2" sx={{ color: COLORS.actual, fontWeight: 500 }}>
              Actual Total: {data.actual} days (Leaves: {data.actualLeaves} + Holidays: {data.holidays})
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ 
              width: 12, 
              height: 12, 
              borderRadius: '50%', 
              bgcolor: COLORS.holidays 
            }} />
            <Typography variant="body2" sx={{ color: COLORS.holidays, fontWeight: 500 }}>
              Company Holidays: {data.holidays} days
            </Typography>
          </Box>
        </Paper>
      );
    }
    return null;
  };

  return (
    <Box sx={{ p: 3,  minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ 
          fontWeight: 'bold',
          background: 'black',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
         Leave Prediction
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Predictive analytics for workforce planning and leave management
        </Typography>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert 
          severity={error.severity || (typeof error === 'string' && error.startsWith('⚠️') ? 'warning' : 'error')} 
          sx={{ mb: 2, borderRadius: '10px', alignItems: 'center' }} 
          onClose={() => setError('')}
          action={
            error?.upgrade_required && (
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                onClick={() => window.dispatchEvent(new CustomEvent("openUpgradeModal", { detail: { feature: "AI Leave Prediction" } }))}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                Upgrade Plan
              </Button>
            )
          }
        >
          {error.message || error}
        </Alert>
      )}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Control Panel */}
      <Card sx={{ mb: 3, boxShadow: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <FormControl sx={{ width: "200px" }}>
                <InputLabel>Year</InputLabel>
                <Select
                  value={selectedYear}
                  label="Year"
                  onChange={(e) => setSelectedYear(e.target.value)}
                >
                  {Array.from(
                    { length: 5 },
                    (_, i) => new Date().getFullYear() - 2 + i
                  ).map(year => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  onClick={handlePredictYear}
                  disabled={predicting}
                  startIcon={predicting ? <CircularProgress size={20} /> : <Refresh />}
                  sx={{ 
                    minWidth: 220,
                    background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                    boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)'
                  }}
                >
                  {predicting ? 'Generating...' : `Predict Entire ${selectedYear}`}
                </Button>
                <Button
                  variant="outlined"
                  onClick={fetchYearPredictions}
                  startIcon={<Refresh />}
                >
                  Refresh Data
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Cards */}
     {analytics && (
  <Grid container spacing={3} sx={{ mb: 3 }}>
    
    {/* Total Employees */}
    <Grid item xs={12} sm={6} md={3}>
      <Card
        sx={{
          backgroundColor: "#fff",
          color: "#1f2937",
          borderLeft: "5px solid #667eea",
          boxShadow: 3,
          borderRadius: 2,
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="h6" color="text.secondary">
                Total Employees
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  mt: 1,
                  fontWeight: "bold",
                }}
              >
                {analytics.summary.totalEmployees}
              </Typography>
            </Box>

            <People
              sx={{
                fontSize: 50,
                color: "#667eea",
                opacity: 0.8,
              }}
            />
          </Box>
        </CardContent>
      </Card>
    </Grid>

    {/* High Risk */}
    <Grid item xs={12} sm={6} md={3}>
      <Card
        sx={{
          backgroundColor: "#fff",
          color: "#1f2937",
          borderLeft: "5px solid #f5576c",
          boxShadow: 3,
          borderRadius: 2,
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="h6" color="text.secondary">
                High Risk
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  mt: 1,
                  fontWeight: "bold",
                }}
              >
                {analytics.summary.highRisk}
              </Typography>
            </Box>

            <Error
              sx={{
                fontSize: 50,
                color: "#f5576c",
                opacity: 0.8,
              }}
            />
          </Box>
        </CardContent>
      </Card>
    </Grid>

    {/* Medium Risk */}
    <Grid item xs={12} sm={6} md={3}>
      <Card
        sx={{
          backgroundColor: "#fff",
          color: "#1f2937",
          borderLeft: "5px solid #f59e0b",
          boxShadow: 3,
          borderRadius: 2,
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="h6" color="text.secondary">
                Medium Risk
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  mt: 1,
                  fontWeight: "bold",
                }}
              >
                {analytics.summary.mediumRisk}
              </Typography>
            </Box>

            <Warning
              sx={{
                fontSize: 50,
                color: "#f59e0b",
                opacity: 0.8,
              }}
            />
          </Box>
        </CardContent>
      </Card>
    </Grid>

    {/* Low Risk */}
    <Grid item xs={12} sm={6} md={3}>
      <Card
        sx={{
          backgroundColor: "#fff",
          color: "#1f2937",
          borderLeft: "5px solid #10b981",
          boxShadow: 3,
          borderRadius: 2,
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="h6" color="text.secondary">
                Low Risk
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  mt: 1,
                  fontWeight: "bold",
                }}
              >
                {analytics.summary.lowRisk}
              </Typography>
            </Box>

            <CheckCircle
              sx={{
                fontSize: 50,
                color: "#10b981",
                opacity: 0.8,
              }}
            />
          </Box>
        </CardContent>
      </Card>
    </Grid>

  </Grid>
)}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
               <Tabs 
          value={tabValue} 
          onChange={(e, v) => setTabValue(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab label="Analytics" />
          <Tab label="Employee Details" />
        </Tabs>

      </Box>

      {/* Analytics Tab */}
      {tabValue === 0 && analytics && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ boxShadow: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Monthly Accuracy Trends</Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={analytics.monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="accuracy" 
                      stroke="#ff7300" 
                      strokeWidth={2}
                      name="Accuracy %"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card sx={{ boxShadow: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Prediction Statistics</Typography>
                <Box sx={{ p: 2 }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Total Predictions</Typography>
                    <Typography variant="h4">{analytics.summary.totalPredictions}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Average Accuracy</Typography>
                    <Typography variant="h4">
                      {(analytics.monthlyData.reduce((sum, m) => sum + parseFloat(m.accuracy), 0) / analytics.monthlyData.length).toFixed(1)}%
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">High Risk Percentage</Typography>
                    <Typography variant="h4">
                      {((analytics.summary.highRisk / analytics.summary.totalPredictions) * 100).toFixed(1)}%
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Employee Details Tab */}
      {tabValue === 1 && (
        <Grid container spacing={3}>
          {/* Employee Selection Card */}
          <Grid item xs={12}>
            <Card sx={{ boxShadow: 3, mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: 1, minWidth: 300 }}>
                    <Typography variant="h6" gutterBottom>Select Employee</Typography>
                    <FormControl fullWidth>
                      <InputLabel>Employee</InputLabel>
                      <Select
                        value={selectedEmployee}
                        label="Employee"
                        onChange={(e) => setSelectedEmployee(e.target.value)}
                      >
                        <MenuItem value="">All Employees</MenuItem>
                        {employees.map(emp => (
                          <MenuItem key={emp.employee_id} value={emp.employee_id}>
                            {emp.firstName} {emp.lastName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  {holidayData && (
                    <Tooltip title="Holiday data available for this year">
                      <Chip 
                        icon={<EventAvailable />}
                        label={`${holidayData.holidays.length} Holidays in ${holidayData.year}`}
                        color="secondary"
                        variant="outlined"
                      />
                    </Tooltip>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Chart */}
          <Grid item xs={12}>
            <Card sx={{ boxShadow: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {selectedEmployee 
                    ? `${employees.find(e => e.employee_id === selectedEmployee)?.firstName} ${employees.find(e => e.employee_id === selectedEmployee)?.lastName} - Monthly Breakdown`
                    : `All Employees - Aggregated Monthly Breakdown (${employees.length} employees)`}
                </Typography>
                <ResponsiveContainer width="100%" height={450}>
                  <LineChart data={getEmployeeMonthlyData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <RechartsTooltip content={<CustomTooltip />} />
                    <Legend />

                    <Line
                      type="monotone"
                      dataKey="predicted"
                      stroke={COLORS.predicted}
                      strokeWidth={3}
                      strokeDasharray="5 5"
                      name="Predicted (Leaves + Holidays)"
                      dot={{ r: 6, stroke: COLORS.predicted, fill: COLORS.predicted }}
                    />

                    <Line
                      type="monotone"
                      dataKey="actual"
                      stroke={COLORS.actual}
                      strokeWidth={3}
                      name="Actual (Leaves + Holidays)"
                      dot={{ r: 6, stroke: COLORS.actual, fill: COLORS.actual }}
                    />

                    <Line
                      type="monotone"
                      dataKey="holidays"
                      stroke={COLORS.holidays}
                      strokeWidth={3}
                      name="Company Holidays"
                      dot={{ r: 7, stroke: COLORS.holidays, fill: COLORS.holidays }}
                      strokeDasharray="3 3"
                    />
                  </LineChart>
                </ResponsiveContainer>
                
                <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                  <Chip 
                    label="Predicted (Leaves + Holidays) - Dashed Red" 
                    sx={{ bgcolor: '#fef2f2', color: COLORS.predicted, border: '2px dashed', borderColor: COLORS.predicted }}
                  />
                  <Chip 
                    label="Actual Total (Leaves + Holidays) - Solid Blue" 
                    sx={{ bgcolor: '#eff6ff', color: COLORS.actual, border: '2px solid', borderColor: COLORS.actual }}
                  />
                  <Chip 
                    label="Company Holidays Only - Dashed Purple" 
                    sx={{ bgcolor: '#f3e5f5', color: COLORS.holidays, border: '2px dashed', borderColor: COLORS.holidays }}
                  />
                </Box>
                <Box sx={{ mt: 1, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    * Red line includes both predicted leaves and company holidays | Purple line shows company holidays only
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Monthly Details Table - Only show for individual employees */}
          {selectedEmployee && (
            <Grid item xs={12}>
              <Card sx={{ boxShadow: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Month-wise Details</Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Month</strong></TableCell>
                          <TableCell><strong>Predicted (Leaves + Holidays)</strong></TableCell>
                          <TableCell><strong>Actual Leaves</strong></TableCell>
                          <TableCell><strong>Company Holidays</strong></TableCell>
                          <TableCell><strong>Total (Leaves + Holidays)</strong></TableCell>
                          <TableCell><strong>Probability</strong></TableCell>
                          <TableCell><strong>Risk</strong></TableCell>
                          <TableCell><strong>Accuracy</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {getEmployeeMonthlyData().map((row, idx) => {
                          const accuracy = row.actual > 0 
                            ? (100 - Math.abs(row.predicted - row.actual) / row.actual * 100).toFixed(0)
                            : 0;
                          return (
                            <TableRow key={idx}>
                              <TableCell>{row.month}</TableCell>
                              <TableCell><strong>{row.predicted} days</strong></TableCell>
                              <TableCell>{row.actualLeaves} days</TableCell>
                              <TableCell>
                                <Chip 
                                  label={`${row.holidays} days`}
                                  size="small"
                                  icon={<EventAvailable />}
                                  sx={{ 
                                    bgcolor: '#f3e5f5',
                                    color: COLORS.holidays
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <strong>{row.actual} days</strong>
                              </TableCell>
                              <TableCell>{row.probability}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={row.riskLevel} 
                                  size="small"
                                  sx={{ 
                                    bgcolor: getRiskLevelColor(row.riskLevel),
                                    color: 'white'
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <LinearProgress 
                                    variant="determinate" 
                                    value={parseFloat(accuracy)} 
                                    sx={{ width: 100 }}
                                  />
                                  <Typography variant="body2">{accuracy}%</Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};

export default PredictionDashboard;